import { Icon, Popover, useDisclosure } from '@chakra-ui/react';
import isString from 'lodash/isString';
import { forwardRef, useId, useImperativeHandle, useState } from 'react';
import FocusLock from 'react-focus-lock';
import { MdClose, MdFilterAlt, MdFilterList } from 'react-icons/md';
import { useNonInitialEffect } from '../../../../hooks/useNonInitialEffect';
import MCustomIconButton from '../../MCustomIconButton';
import MCustomRadioGroup from '../../MCustomRadioGroup';
import { FOCUS_WITHOUT_SCROLLING } from '../../Overlays';
import {
  BooleanOption,
  DataTableFilterRef,
  FilterProps,
} from '../DataTableTypes';
import { getColumnFilterKey } from '../tableUtilsNew';

const ALL: BooleanOption = 'ALL';
const CHECKED: BooleanOption = 'CHECKED';
const UNCHECKED: BooleanOption = 'UNCHECKED';

const OPTIONS = [
  {
    title: 'All',
    value: ALL,
  },
  {
    title: 'Yes',
    value: CHECKED,
  },
  {
    title: 'No',
    value: UNCHECKED,
  },
];

export const DataTableHeaderBooleanFilter = forwardRef<
  DataTableFilterRef,
  FilterProps<any>
>(({ column, table, clientControlled }, ref) => {
  const key = getColumnFilterKey(column);

  // Opt-in per-column config (ColumnMeta.booleanFilter). Its presence enables
  // restoring the applied value on load and a non-'ALL' default; columns
  // without it behave exactly as before. Option labels come from the shared
  // OPTIONS, not from here.
  const booleanFilterConfig = column.columnDef.meta?.booleanFilter;
  const defaultValue: BooleanOption = booleanFilterConfig?.defaultValue ?? ALL;

  const getInitialValue = (): BooleanOption => {
    if (!booleanFilterConfig) {
      return ALL;
    }
    const existing = table.options.meta?.selectedFilters?.get(key);
    if (existing === ALL || existing === CHECKED || existing === UNCHECKED) {
      return existing as BooleanOption;
    }
    return ALL;
  };

  const [selectedValue, setSelectedValue] =
    useState<BooleanOption>(getInitialValue);

  useImperativeHandle(ref, (): DataTableFilterRef => ({
    resetFilters: (clearToEmpty?: boolean) => {
      setSelectedValue(clearToEmpty ? ALL : defaultValue);
    },
    setFilterValue: ({ columnKey, value }) => {
      if (
        columnKey === column.id &&
        isString(value) &&
        [ALL, CHECKED, UNCHECKED].includes(value as BooleanOption)
      ) {
        setSelectedValue(value as BooleanOption);
      }
    },
  }));

  const { open, onOpen, onClose } = useDisclosure();

  useNonInitialEffect(() => {
    if (table.options.meta?.onFilterUpdate) {
      table.options.meta.onFilterUpdate(key, selectedValue);
    }

    if (clientControlled) {
      // For client-controlled, update immediately
      column.setFilterValue(selectedValue);
    }
  }, [selectedValue, clientControlled]);

  const handleClose = () => {
    onClose();
  };

  const filtersApplied = selectedValue !== 'ALL';

  const titleId = useId();

  return (
    <Popover.Root
      open={open}
      lazyMount
      unmountOnExit
      onOpenChange={(e) => {
        if (e.open) {
        } else {
          handleClose();
        }
      }}
      positioning={{
        placement: 'bottom-start',
        offset: { mainAxis: 0, crossAxis: 0 },
        strategy: 'fixed',
      }}
    >
      <Popover.Trigger asChild>
        <MCustomIconButton
          data-testid={`table-column-filter-${key}`}
          btnSize={5}
          onClick={onOpen}
          icon={filtersApplied ? MdFilterAlt : MdFilterList}
          aria-label="Filter"
          variant="icon"
          ml="2"
          color={filtersApplied ? 'tIndigo.base' : 'tPurple.base'}
          iconColor={filtersApplied ? 'tIndigo.base' : 'tPurple.base'}
        />
      </Popover.Trigger>
      <Popover.Positioner>
        <Popover.Content aria-labelledby={titleId}>
          {open && (
            <FocusLock focusOptions={FOCUS_WITHOUT_SCROLLING}>
              <Popover.Arrow>
                <Popover.ArrowTip />
              </Popover.Arrow>
              <Popover.CloseTrigger aria-label="Close">
                <Icon asChild>
                  <MdClose />
                </Icon>
              </Popover.CloseTrigger>
              <Popover.Header id={titleId}>Filter</Popover.Header>
              <Popover.Body minW="304px">
                <MCustomRadioGroup
                  value={selectedValue}
                  my={1}
                  itemTitle="title"
                  itemValue="value"
                  items={OPTIONS}
                  onChange={(value) => setSelectedValue(value as BooleanOption)}
                />
              </Popover.Body>
            </FocusLock>
          )}
        </Popover.Content>
      </Popover.Positioner>
    </Popover.Root>
  );
});
